import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  motion,
  useSpring,
  useMotionValue,
  AnimatePresence,
} from "framer-motion";
// ----REMOVE THIS COMPONENT IF NOT NEEDED ALSO REMOVE FROM APP.TSX (MOUSE_ANIMATION)
export const MouseFollower: React.FC = () => {
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);

  // Define 6 different layers of trailing rings with higher visibility
  const ringConfigs = [
    { damping: 15, stiffness: 400, size: 8, opacity: 1, isDot: true }, // Inner dot
    {
      damping: 20,
      stiffness: 250,
      size: 36,
      opacity: 0.8,
      border: true,
      borderWidth: 2,
    }, // Main ring
    {
      damping: 25,
      stiffness: 200,
      size: 52,
      opacity: 0.6,
      border: true,
      borderWidth: 1.5,
    }, // Trail 1
    {
      damping: 30,
      stiffness: 150,
      size: 68,
      opacity: 0.4,
      border: true,
      borderWidth: 1,
    }, // Trail 2
    {
      damping: 35,
      stiffness: 100,
      size: 84,
      opacity: 0.3,
      border: true,
      borderWidth: 1,
    }, // Trail 3
    {
      damping: 40,
      stiffness: 80,
      size: 104,
      opacity: 0.2,
      border: true,
      borderWidth: 1,
    }, // Trail 4
  ];

  // Generate springs for each ring
  const springs = ringConfigs.map((config) => ({
    x: useSpring(mouseX, {
      damping: config.damping,
      stiffness: config.stiffness,
    }),
    y: useSpring(mouseY, {
      damping: config.damping,
      stiffness: config.stiffness,
    }),
    ...config,
  }));

  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isVisible) setIsVisible(true);
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
    },
    [isVisible, mouseX, mouseY],
  );

  const handleMouseOver = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const isInteractive = !!(
      target.tagName === "A" ||
      target.tagName === "BUTTON" ||
      target.closest("a") ||
      target.closest("button") ||
      target.getAttribute("role") === "button"
    );

    setIsHovering(isInteractive);
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseover", handleMouseOver);
    document.body.style.cursor = "none";

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseover", handleMouseOver);
      document.body.style.cursor = "auto";
    };
  }, [handleMouseMove, handleMouseOver]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[100001] overflow-hidden">
      <AnimatePresence>
        {isVisible && (
          <>
            {/* Intensified Hover Background Glow */}
            <motion.div
              style={{
                x: springs[1].x,
                y: springs[1].y,
                translateX: "-50%",
                translateY: "-50%",
              }}
              animate={{
                opacity: isHovering ? 0.6 : 0.1,
                scale: isHovering ? 6 : 2,
                backgroundColor: isHovering
                  ? "rgba(242, 116, 13, 0.4)"
                  : "rgba(14, 165, 164, 0.2)",
              }}
              className="absolute w-12 h-12 rounded-full blur-3xl transition-all duration-500"
            />

            {/* High-Visibility Trailing Rings */}
            {springs.map((spring, i) => (
              <motion.div
                key={i}
                style={{
                  x: spring.x,
                  y: spring.y,
                  translateX: "-50%",
                  translateY: "-50%",
                  width: spring.size,
                  height: spring.size,
                  borderWidth: spring.borderWidth || 1,
                }}
                animate={{
                  scale: isHovering ? 1.4 + i * 0.15 : 1,
                  opacity: isHovering ? spring.opacity : spring.opacity * 0.8,
                  borderColor: isHovering
                    ? "rgba(242, 116, 13, 0.8)"
                    : "rgba(14, 165, 164, 0.7)",
                  backgroundColor: spring.isDot
                    ? isHovering
                      ? "#f2740d"
                      : "#0ea5a4"
                    : isHovering
                      ? "rgba(242, 116, 13, 0.1)"
                      : "rgba(14, 165, 164, 0.03)",
                }}
                className={`absolute rounded-full ${spring.border ? "border" : ""} ${i === 1 ? "backdrop-blur-[2px]" : ""} transition-colors duration-300 shadow-[0_0_10px_rgba(14,165,164,0.1)]`}
              />
            ))}

            {/* Strong Magnetic Accent Pulse */}
            <motion.div
              style={{
                x: springs[0].x,
                y: springs[0].y,
                translateX: "-50%",
                translateY: "-50%",
              }}
              animate={{
                scale: isHovering ? [1, 1.3, 1] : 0,
                opacity: isHovering ? 0.8 : 0,
              }}
              transition={{ repeat: Infinity, duration: 1.2 }}
              className="absolute w-6 h-6 rounded-full border-2 border-accent-500/60 shadow-[0_0_15px_rgba(242,116,13,0.3)]"
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
